import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { ChevronDown, ChevronUp, ShieldCheck, Stamp, X } from 'lucide-react';
import { captureEvent } from '../../lib/analytics/posthog';
import { cn } from '../../lib/utils';

interface DocumentActionBarProps {
  showSignWithSima: boolean;
  showRelease: boolean;
  isUpdatingRegistryStatus: boolean;
  hasSignerId: boolean;
  onSignWithSima: () => void;
  onRelease: () => void;
  onReject: () => void;
  /** Collapsible floating bar on mobile; inline footer on desktop metadata panel */
  variant?: 'mobile' | 'desktop';
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  className?: string;
}

const DocumentActionBar: React.FC<DocumentActionBarProps> = ({
  showSignWithSima,
  showRelease,
  isUpdatingRegistryStatus,
  hasSignerId,
  onSignWithSima,
  onRelease,
  onReject,
  variant = 'desktop',
  expanded = false,
  onExpandedChange,
  className,
}) => {
  const { t } = useLanguage();

  const handleSignWithSima = () => {
    captureEvent('document_sign_with_sima_clicked', { surface: variant });
    onSignWithSima();
  };

  const handleRelease = () => {
    captureEvent('document_release_clicked', { surface: variant });
    onRelease();
  };

  const handleReject = () => {
    captureEvent('document_reject_clicked', { surface: variant });
    onReject();
  };

  const actionButtons = (
    <>
      {showSignWithSima && (
        <Button className="w-full" onClick={handleSignWithSima}>
          <ShieldCheck className="h-4 w-4" />
          {t('signWithSima')}
        </Button>
      )}
      {showRelease && (
        <Button
          type="button"
          className="w-full bg-yellow-400 text-yellow-950 hover:bg-yellow-500 dark:bg-yellow-500 dark:text-yellow-950 dark:hover:bg-yellow-600"
          onClick={handleRelease}
          disabled={isUpdatingRegistryStatus || !hasSignerId}
        >
          <Stamp className="h-4 w-4" />
          {t('release')}
        </Button>
      )}
      <Button
        type="button"
        className="w-full"
        variant="destructive"
        onClick={handleReject}
        disabled={isUpdatingRegistryStatus || !hasSignerId}
      >
        <X className="h-4 w-4" />
        {t('reject')}
      </Button>
    </>
  );

  if (variant === 'mobile') {
    const floatingShell = 'rounded-xl border bg-background/95 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80';

    return (
      <div
        className={cn(
          'fixed bottom-[max(0.75rem,env(safe-area-inset-bottom))] left-3 right-3 z-20',
          className,
        )}
      >
        {expanded ? (
          <div className={cn(floatingShell, 'space-y-2 p-3')}>
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-md px-1 py-1 text-sm font-medium text-foreground"
              onClick={() => {
                captureEvent('document_actions_panel_toggled', { expanded: false });
                onExpandedChange?.(false);
              }}
              aria-expanded
              aria-label={t('documentActionsCollapse')}
            >
              <span>{t('documentActions')}</span>
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            </button>
            {actionButtons}
          </div>
        ) : (
          <button
            type="button"
            className={cn(
              floatingShell,
              'flex w-full items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-foreground',
            )}
            onClick={() => {
              captureEvent('document_actions_panel_toggled', { expanded: true });
              onExpandedChange?.(true);
            }}
            aria-expanded={false}
            aria-label={t('documentActionsExpand')}
          >
            <ShieldCheck className="h-4 w-4 shrink-0 text-primary" aria-hidden />
            <span>{t('documentActions')}</span>
            <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={cn('shrink-0 space-y-2 border-t bg-background p-4', className)}>
      {actionButtons}
    </div>
  );
};

export default DocumentActionBar;
